import { useEffect, useState } from "react";
import { useT } from "../../i18n/hooks.ts";
import {
  findSubBasinsWithSeriesGap,
  isSeriesScaleLocked,
  seriesGapRange,
  type DailySeries,
} from "../../model/dailySeries.ts";
import { aemetJobStore, type AemetQueuedRequest } from "../../model/aemet/aemetJobStore.ts";
import { subBasinCentroid, subBasinGeometry } from "../../model/gisLayer.ts";
import {
  allScalesFormatDescription,
  pasteFormatKey,
  type SeriesScale,
} from "../../model/seriesScale.ts";
import type { Aquifer, EvalHidProject, SubBasin } from "../../model/types.ts";
import { mostCommonMethod } from "../aemet/aemetBulkMethod.ts";
import { InfoIcon } from "../InfoIcon.tsx";
import { InlineProgress } from "../InlineProgress.tsx";
import { SeriesLoader } from "../SeriesLoader.tsx";
import type { SeriesSelection } from "../mapModes.ts";
import { SeriesChart } from "./SeriesChart.tsx";
import { useProjectEditing } from "./hooks.ts";

/** Applies an entity update, folding in an inferred scale (from the FIRST series ever loaded into this project) when present — one project write, not two (see SeriesLoader.tsx's own `onEntitiesUpdated` doc comment). */
function applyProjectUpdate(
  project: EvalHidProject,
  onChangeProject: (project: EvalHidProject) => void,
  changes: Partial<EvalHidProject>,
  inferredScale?: SeriesScale
) {
  let p = { ...project, ...changes };
  if (inferredScale) p = { ...p, seriesScale: inferredScale };
  onChangeProject(p);
}

/** First entity (by array order) with its own column in `series` — the default SeriesChart.tsx shows before the user has clicked anything on the map/cards (see MapSeriesMode). */
function firstLinkedCode<T>(
  entities: T[],
  series: DailySeries | null,
  entityCodeOf: (entity: T) => number,
  columnIndexOf: (entity: T) => number
): number | undefined {
  if (!series) return undefined;
  const first = entities.find((e) => {
    const i = columnIndexOf(e);
    return i > 0 && i - 1 < series.columns.length;
  });
  return first ? entityCodeOf(first) : undefined;
}

/** What the pumping chart below actually plots — see `resolvePumpingChartEntity`'s own comment for why this can't just be an `Aquifer` (unlike precipitation/ETP/temperature, which only ever link sub-basins). */
interface PumpingChartEntity {
  code: number;
  name: string;
  pumpingCol: number;
}

function hasOwnColumn(pumpingCol: number, series: DailySeries): boolean {
  return pumpingCol > 0 && pumpingCol - 1 < series.columns.length;
}

/**
 * Which entity the pumping chart shows — unlike precipitation/ETP/
 * temperature (always a sub-basin), `Ent_Bomb.csv`/`series.pumping` links
 * BOTH aquifers AND sub-basins by name, two fully independent resolutions
 * (see `SubBasin.pumpingCol`'s own doc comment in types.ts) — a project
 * with pumping linked only to sub-basins and no aquifers at all is a real,
 * valid case (Op_bombeos doesn't require any aquifer to exist), and used
 * to show no chart whatsoever, since this only ever looked at
 * `project.aquifers`. Prefers the map/card selection's own kind when it
 * has a real column (an explicit aquifer click shouldn't silently fall
 * back to a sub-basin, or vice versa); otherwise the first aquifer with a
 * column, then the first sub-basin with one.
 */
function resolvePumpingChartEntity(
  aquifers: Aquifer[],
  subBasins: SubBasin[],
  series: DailySeries | null,
  selection: SeriesSelection | null
): PumpingChartEntity | null {
  if (!series) return null;
  if (selection?.kind === "aquifer") {
    const a = aquifers.find((a) => a.aquiferCode === selection.id);
    if (a && hasOwnColumn(a.pumpingCol, series)) {
      return { code: a.aquiferCode, name: a.name, pumpingCol: a.pumpingCol };
    }
  }
  if (selection?.kind === "subBasin") {
    const c = subBasins.find((c) => c.subBasinCode === selection.id);
    if (c && hasOwnColumn(c.pumpingCol, series)) {
      return { code: c.subBasinCode, name: c.description, pumpingCol: c.pumpingCol };
    }
  }
  const firstAquifer = aquifers.find((a) => hasOwnColumn(a.pumpingCol, series));
  if (firstAquifer) {
    return {
      code: firstAquifer.aquiferCode,
      name: firstAquifer.name,
      pumpingCol: firstAquifer.pumpingCol,
    };
  }
  const firstSubBasin = subBasins.find((c) => hasOwnColumn(c.pumpingCol, series));
  if (firstSubBasin) {
    return {
      code: firstSubBasin.subBasinCode,
      name: firstSubBasin.description,
      pumpingCol: firstSubBasin.pumpingCol,
    };
  }
  return null;
}

/**
 * Uploads or replaces the input series of an ALREADY EXISTING project —
 * needed when the project was opened without being able to read one
 * (scenario folder not found, file moved/deleted...): before this there
 * was no way to fix it from the app other than recreating the whole
 * project, even though the "the ... series hasn't loaded" notices
 * (validations.ts) did flag it as blocking. Reuses the same
 * `SeriesLoader` from the creation wizard (Step5Series.tsx) — same format
 * validation and the same notice about how many elements have their own
 * column.
 *
 * Below each `SeriesLoader`, a `SeriesChart` for whichever sub-basin/
 * aquifer is currently selected on the main map or in the card view (see
 * MapSeriesMode, SubBasinMap.tsx/SubBasinCanvas.tsx) — `seriesSelection`
 * drives precipitation/ETP/temperature together (they all key off the same
 * sub-basins), falling back to the first entity with its own column until
 * the user picks one explicitly. Pumping is the odd one out: it can link
 * EITHER an aquifer or a sub-basin (see `resolvePumpingChartEntity`), so
 * its chart follows `seriesSelection` regardless of which kind it is. That
 * fallback is written back via `onChangeSeriesSelection` the first time
 * this tab is open with nothing selected yet, so the map/cards highlight
 * it too instead of only the charts silently agreeing on it — see the
 * effect below.
 */
export function SeriesPanel({
  seriesSelection,
  onChangeSeriesSelection,
  onOpenBulkAemet,
}: {
  seriesSelection: SeriesSelection | null;
  onChangeSeriesSelection: (selection: SeriesSelection) => void;
  /** Opens AemetBulkFetchDialog.tsx (and closes this whole editor panel first) — see its own button below. An optional sub-basin code list pre-scopes the dialog instead of the usual "whole project" (see the gap notice below). */
  onOpenBulkAemet: (onlyCodes?: number[]) => void;
}) {
  const t = useT();
  const {
    project,
    onChangeProject,
    series,
    onChangeSeries: onChange,
    gisLayer,
    aemetProvenance,
  } = useProjectEditing();

  const [loadingPrecip, setLoadingPrecip] = useState(false);
  const [loadingEtp, setLoadingEtp] = useState(false);
  const [loadingTemp, setLoadingTemp] = useState(false);
  const [loadingPumping, setLoadingPumping] = useState(false);
  // Progress for "Complete these N" below — set right before firing off the
  // fetch, cleared once every group settles (see completeGaps).
  const [completingGaps, setCompletingGaps] = useState<{
    targetCodes: Set<number>;
    total: number;
  } | null>(null);

  const selectedSubBasin =
    seriesSelection?.kind === "subBasin"
      ? seriesSelection.id
      : firstLinkedCode(
          project.subBasins,
          series.precipitation,
          (c) => c.subBasinCode,
          (c) => c.precipCol
        );
  const pumpingEntity = resolvePumpingChartEntity(
    project.aquifers,
    project.subBasins,
    series.pumping,
    seriesSelection
  );

  // Seeds the shared map/card selection with the default sub-basin the
  // first time this tab opens with nothing selected yet — without this,
  // the charts already show it (via the fallback above) but the map/cards
  // show no highlight at all until the user clicks something, which read
  // as "selection broken" even though the charts were already correct.
  // Only fires once: setting `seriesSelection` makes the condition below
  // false on the next render, same self-terminating pattern as
  // MitecoSubBasinsLayer.tsx's own fetch-on-mount effect.
  useEffect(() => {
    if (seriesSelection !== null || selectedSubBasin === undefined) return;
    onChangeSeriesSelection({ kind: "subBasin", id: selectedSubBasin });
  }, [seriesSelection, selectedSubBasin, onChangeSeriesSelection]);

  const scaleLocked = isSeriesScaleLocked(series);
  const isDaily = project.seriesScale === "daily";
  // The CSV upload accepts one extra shape for monthly (dd/mm/aaaa with the
  // day fixed at 01, the other real desktop convention — see
  // seriesScale.ts's own header) that paste doesn't offer, so this needs
  // its own text instead of just reusing pasteFormatKey's single canonical
  // format. Before the scale is decided at all, every known scale's own
  // description is listed (`allScalesFormatDescription`) instead of just
  // one — whichever is uploaded first is what decides it (see
  // SeriesLoader.tsx's own `onFile`).
  const uploadFormatHint = !scaleLocked
    ? allScalesFormatDescription(t)
    : isDaily
      ? t(pasteFormatKey("daily"))
      : t("seriesScale.monthlyFormatDescription");
  // Appended AFTER the whole "CSV ... fecha {format} y una columna por..."
  // sentence (formatHelpSubBasin/formatHelpAquifer below) instead of being
  // folded into `uploadFormatHint` itself — interpolating it into the
  // middle of that sentence (inside the `{format}` slot) used to read as a
  // parenthetical dropped mid-clause ("fecha X — explanation — y una
  // columna..."), not as its own remark.
  const autoDetectSuffix = scaleLocked ? "" : ` ${t("seriesScale.autoDetectSuffix")}`;

  // Sub-basins with a NaN gap somewhere in their currently loaded
  // precipitation/ETP, surfaced WITHOUT having to calculate first — see
  // findSubBasinsWithSeriesGap's own comment (dailySeries.ts) for why this
  // isn't just engine.ts's post-calculation findTruncatedSubBasins reused
  // here (that one skips sub-basins with no model assigned yet, which this
  // panel has no business caring about). Real incident (2026-08, Duero:
  // 1728 sub-basins): a bulk "Ampliar" that fails partway through leaves a
  // handful of sub-basins with a real gap in the middle of an otherwise-
  // existing column — nothing in this panel used to say so, the only way
  // to find out was running a calculation and reading the warning, then
  // fixing each affected sub-basin one at a time from its own dialog.
  const gappedSubBasins = findSubBasinsWithSeriesGap(
    project.subBasins,
    series.precipitation,
    series.etp
  );

  // Re-fetches exactly the gapped window of each affected sub-basin's own
  // column, launched directly — no dialog to configure a method/range in
  // first (AemetBulkFetchDialog.tsx's "extend" mode used to open here, but
  // it derives its range from the PROJECT's shared date axis, which for
  // these rows is the wrong reference point: their own gap can be nowhere
  // near either edge of it, and "Años anteriores" past whatever the active
  // method's own real coverage floor is has no valid range to offer at
  // all — see AemetExtendBody's own comment on `forcedRange`). Grouped by
  // identical gap range (typically all of them, when they're leftovers
  // from the SAME interrupted bulk extend) so each distinct window is only
  // requested once per sub-basin instead of once per group member.
  async function completeGaps() {
    const method = mostCommonMethod(aemetProvenance) ?? "nearest";
    const groups = new Map<
      string,
      { range: { start: string; end: string }; requests: AemetQueuedRequest[] }
    >();
    for (const sb of gappedSubBasins) {
      const range = seriesGapRange(sb, series.precipitation, series.etp);
      const centroid = gisLayer ? subBasinCentroid(gisLayer, sb.subBasinCode) : null;
      if (!range || !centroid) continue;
      const key = `${range.start}|${range.end}`;
      const group = groups.get(key) ?? { range, requests: [] };
      group.requests.push({
        subBasinCode: sb.subBasinCode,
        subBasinName: sb.description,
        centroid,
        opts: {
          extendRange: range,
          method,
          geometry: gisLayer ? subBasinGeometry(gisLayer, sb.subBasinCode) : null,
        },
      });
      groups.set(key, group);
    }
    if (groups.size === 0) return;

    setCompletingGaps({
      targetCodes: new Set(gappedSubBasins.map((sb) => sb.subBasinCode)),
      total: gappedSubBasins.length,
    });
    try {
      await Promise.all(
        Array.from(groups.values()).map((g) =>
          aemetJobStore.startQueuedExtendWithRetry(g.requests, g.range)
        )
      );
    } finally {
      setCompletingGaps(null);
    }
  }

  return (
    <div>
      <button
        className="button series-panel-fetch-aemet"
        onClick={() => onOpenBulkAemet()}
        disabled={!isDaily}
        title={isDaily ? undefined : t("seriesScale.aemetDailyOnly")}
      >
        {t("seriesPanel.fetchFromAemet")}
      </button>

      {gappedSubBasins.length > 0 &&
        (completingGaps ? (
          <InlineProgress
            text={t("seriesPanel.gapWarningCompleting", {
              done: String(
                completingGaps.total -
                  gappedSubBasins.filter((sb) => completingGaps.targetCodes.has(sb.subBasinCode))
                    .length
              ),
              total: String(completingGaps.total),
            })}
          />
        ) : (
          <p className="series-gap-notice">
            {gappedSubBasins.length === 1
              ? t("seriesPanel.gapWarningOne")
              : t("seriesPanel.gapWarningMultiple", { n: gappedSubBasins.length })}{" "}
            <button className="button-icon" onClick={() => void completeGaps()}>
              {t("seriesPanel.gapWarningComplete", { n: gappedSubBasins.length })}
            </button>
          </p>
        ))}

      <h4>
        {t("infoPopover.precipitation")}{" "}
        <InfoIcon text={t("seriesPanel.mapSelectionHelpSubBasin")} />
      </h4>
      <SeriesChart
        series={series.precipitation}
        entities={project.subBasins}
        entityCodeOf={(c) => c.subBasinCode}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.precipCol}
        selectedCode={selectedSubBasin}
        scale={project.seriesScale}
        loading={loadingPrecip}
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.precipitation")}
        required
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.precipCol}
        withIndex={(c, index) => ({ ...c, precipCol: index })}
        series={series.precipitation}
        onLoaded={(s) => onChange({ ...series, precipitation: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
        showLabel={false}
        formatHelp={
          t("seriesPanel.formatHelpSubBasin", { format: uploadFormatHint }) + autoDetectSuffix
        }
        onLoadingChange={setLoadingPrecip}
      />

      <h4>
        {t("infoPopover.etp")} <InfoIcon text={t("seriesPanel.mapSelectionHelpSubBasin")} />
      </h4>
      <SeriesChart
        series={series.etp}
        entities={project.subBasins}
        entityCodeOf={(c) => c.subBasinCode}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.etpCol}
        selectedCode={selectedSubBasin}
        scale={project.seriesScale}
        loading={loadingEtp}
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.etp")}
        required
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.etpCol}
        withIndex={(c, index) => ({ ...c, etpCol: index })}
        series={series.etp}
        onLoaded={(s) => onChange({ ...series, etp: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
        showLabel={false}
        formatHelp={
          t("seriesPanel.formatHelpSubBasin", { format: uploadFormatHint }) + autoDetectSuffix
        }
        onLoadingChange={setLoadingEtp}
      />

      <h4>
        {t("infoPopover.temperature")} {t("common.optional")}{" "}
        <InfoIcon text={t("seriesPanel.mapSelectionHelpSubBasin")} />
      </h4>
      <SeriesChart
        series={series.temperature}
        entities={project.subBasins}
        entityCodeOf={(c) => c.subBasinCode}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.tempCol}
        selectedCode={selectedSubBasin}
        scale={project.seriesScale}
        loading={loadingTemp}
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.temperature")}
        required={false}
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.tempCol}
        withIndex={(c, index) => ({ ...c, tempCol: index })}
        series={series.temperature}
        onLoaded={(s) => onChange({ ...series, temperature: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
        showLabel={false}
        formatHelp={
          t("seriesPanel.formatHelpSubBasin", { format: uploadFormatHint }) + autoDetectSuffix
        }
        onLoadingChange={setLoadingTemp}
      />

      <h4>
        {t("infoPopover.pumping")} {t("common.optional")}{" "}
        <InfoIcon text={t("seriesPanel.mapSelectionHelpPumping")} />
      </h4>
      <SeriesChart
        series={series.pumping}
        entities={pumpingEntity ? [pumpingEntity] : []}
        entityCodeOf={(e) => e.code}
        nameOf={(e) => e.name}
        columnIndexOf={(e) => e.pumpingCol}
        selectedCode={pumpingEntity?.code}
        scale={project.seriesScale}
        loading={loadingPumping}
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.pumping")}
        required={false}
        entities={project.aquifers}
        entityPlural={t("common.aquiferPlural")}
        nameOf={(a) => a.name}
        columnIndexOf={(a) => a.pumpingCol}
        withIndex={(a, index) => ({ ...a, pumpingCol: index })}
        series={series.pumping}
        onLoaded={(s) => onChange({ ...series, pumping: s })}
        // Both groups' freshly-resolved entities (and any inferred scale)
        // arrive together here (see SeriesLoader's own doc comment on why
        // this must be ONE call, not two/three separate onChangeProject
        // calls).
        onEntitiesUpdated={(aquifers, subBasins, inferredScale) =>
          applyProjectUpdate(
            project,
            onChangeProject,
            { aquifers, ...(subBasins ? { subBasins } : {}) },
            inferredScale
          )
        }
        // Ent_Bomb.csv links sub-basins by their OWN name too, independent
        // of any aquifer's own column — see SubBasin.pumpingCol's own doc
        // comment.
        secondaryGroup={{
          entities: project.subBasins,
          entityPlural: t("topbar.subBasins"),
          nameOf: (c) => c.description,
          columnIndexOf: (c) => c.pumpingCol,
          withIndex: (c, index) => ({ ...c, pumpingCol: index }),
        }}
        showLabel={false}
        formatHelp={
          t("seriesPanel.formatHelpAquifer", { format: uploadFormatHint }) + autoDetectSuffix
        }
        onLoadingChange={setLoadingPumping}
      />
    </div>
  );
}
